import apiService from './ApiService';
import { GetFieldAppLayerDataApi } from '../utils/urlConst';

export interface LayerData {
    typeName: string;
    project_id: number;
    task_id: number;
    layer_id: number;
    species_group_id?: number;
    species_subgroup_id?: number;
    version: string;
    srs: string;
}

export interface LayerDataResponse {
    data: LayerData[];
    status: boolean;
    status_code: number;
    status_message: string;
}

class LayerDataService {
    /**
     * Get the workspace namespace from env or default to 'fv'
     */
    private getWorkSpace(): string {
        return import.meta.env.VITE_APP_GEOSERVER_WORKSPACE;
    }
    /**
     * Fetch layer data from Laravel API
     * @param projectId Project ID
     * @param taskId Task ID  
     * @param layerId Layer ID
     * @returns Promise<LayerData[]>
     */
    public async getLayerData(projectId: number, taskId: number, layerId: number | null): Promise<LayerData[]> {
        try {
            // Only include layerId if not null
            const params: any = { projectId, taskId };
            if (layerId !== null && layerId !== undefined) {
                params.layerId = layerId;
            }
            const response = await apiService.get<LayerDataResponse>(GetFieldAppLayerDataApi, params);

            if (response && response.status === true && response.status_code === 1) {
                const layerData = response.data || [];

                // Validate the received data
                const validation = this.validateLayerData(layerData);
                if (!validation.valid) {
                    console.warn('Layer data validation issues:', validation.issues);
                }

                return layerData;
            } 
            else if (response && response.status_code === 0) {
                return [];
            } else {
                throw new Error(response?.status_message || 'Failed to fetch layer data');
            }
        } catch (error) {
            console.error('Error fetching layer data:', error);
            throw error;
        }
    }

    /**
     * Get WMS URL for a specific layer through the proxy
     * @param layerData Layer data from API
     * @param proxy Proxy base URL
     * @returns WMS URL string
     */
    public getWMSUrl(layerData: LayerData, proxy: string): string {
        const { project_id, layer_id, task_id, typeName} = layerData;
        const workSpace = this.getWorkSpace();
        // Base WMS URL with timestamp as seen in proxy logs
        let wmsUrl = `${proxy}/${workSpace}/wms/0/project/${project_id}`;
        if (typeName === `${workSpace}:obs_species`) {
            if (task_id) {
                wmsUrl += `/task/${task_id}`;
            }
            return wmsUrl;
        }
        
        // Add layer_id
        if (layer_id) {
            wmsUrl += `/layer/${layer_id}`;
        }                
    return wmsUrl;
    }

    /**
     * Create WMS parameters for OpenLayers TileWMS source
     * @param layerData Layer data
     * @param styles Optional STYLES parameter for custom styling
     * @returns WMS parameters object
     */
    public createWMSParams(layerData: LayerData, styles?: string): Record<string, any> {
        const params: Record<string, any> = {
            LAYERS: layerData.typeName,
            TILED: true,
            VERSION: '1.3.0',
            FORMAT: 'image/png',
            TRANSPARENT: true,
            CRS: layerData.srs || 'EPSG:3857'
        };

        // Add STYLES parameter
        params.STYLES = styles || '';
        return params;
    }

    /**
     * Create a comprehensive layer configuration object
     * @param layerData Layer data from API
     * @param proxy Proxy base URL
     * @param styles Optional styles
     * @returns Complete layer configuration
     */
    public createLayerConfig(layerData: LayerData, proxy: string, styles?: string) {
        return {
            url: this.getWMSUrl(layerData, proxy),
            params: this.createWMSParams(layerData, styles),
            layerData: layerData,
            displayName: this.getLayerDisplayName(layerData.typeName)
        };
    }

    /**
     * Get SLD style name for a layer
     * @param typeName Layer type name
     * @returns SLD style name
     */
    public getSLDStyleName(layerData: LayerData): string | undefined {
        const workSpace = this.getWorkSpace();
        if (layerData.typeName === `${workSpace}:obs_species`) {
            // if (layerData.species_group_id && layerData.species_subgroup_id) {
            //     console.log(`${workSpace}_obs_species_${layerData.species_group_id}_${layerData.species_subgroup_id}`);
            //     return `${workSpace}_obs_species_${layerData.species_group_id}_${layerData.species_subgroup_id}`;
            // }
            return "";
        }
        const sldStyles: Record<string, string> = {
            [`${workSpace}:plan_areas`]: `${workSpace}_plan_areas`,
            [`${workSpace}:obs_areas`]: `${workSpace}_obs_areas`,
            [`${workSpace}:obs_neighborhood`]: `${workSpace}_obs_neighborhood`,
            [`${workSpace}:report_areas`]: `${workSpace}_report_areas`,
        };
        const style = sldStyles[layerData.typeName] || "";
        return style;
    }

    /**
     * Get a human-readable display name for a layer
     * @param typeName Layer type name
     * @returns Display name
     */
    private getLayerDisplayName(typeName: string): string {
        const workSpace = this.getWorkSpace();
        const displayNames: Record<string, string> = {
            [`${workSpace}:plan_areas`]: 'Plangebieden',
            [`${workSpace}:obs_areas`]: 'Onderzoek',
            [`${workSpace}:obs_neighborhood`]: 'Onderzoek',
            [`${workSpace}:obs_species`]: 'Species',
            [`${workSpace}:report_areas`]: 'Rapportgebieden',
            [`${workSpace}:smp_areas`]: 'SMP',
        };
        return displayNames[typeName] || typeName.replace(`${workSpace}:`, '').replace('_', ' ');
    }

    /**
     * Validate layer data structure
     * @param layerData Array of layer data to validate
     * @returns Validation result with any issues found
     */
    public validateLayerData(layerData: LayerData[]): { valid: boolean; issues: string[] } {
        const issues: string[] = [];

        if (!Array.isArray(layerData)) {
            issues.push('Layer data is not an array');
            return { valid: false, issues };
        }

        layerData.forEach((layer, index) => {
            if (!layer.typeName) {
                issues.push(`Layer ${index}: Missing typeName`);
            }
            if (!layer.project_id) {
                issues.push(`Layer ${index}: Missing project_id`);
            }
            if (!layer.version) {
                issues.push(`Layer ${index}: Missing version`);
            }
            if (!layer.srs) {
                issues.push(`Layer ${index}: Missing srs (coordinate system)`);
            }
        });

        return { valid: issues.length === 0, issues };
    }
}

const layerDataService = new LayerDataService();
export default layerDataService;